import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Metric {
  label: string;
  value: string;
  improvement: string;
}

interface CaseStudy {
  id: number;
  company: string;
  industry: string;
  companySize: string;
  useCase: string;
  image: string;
  alt: string;
  challenge: string;
  solution: string;
  timeline: string;
  metrics: Metric[];
  executiveQuote: string;
  executiveName: string;
  executiveTitle: string;
}

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
  onViewDetails: (id: number) => void;
}

const CaseStudyCard = ({ caseStudy, onViewDetails }: CaseStudyCardProps) => {
  return (
    <div className="bg-card rounded-lg shadow-subtle overflow-hidden hover:shadow-authority transition-all duration-300">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={caseStudy.image}
          alt={caseStudy.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 left-4">
          <span className="inline-block px-3 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
            {caseStudy.industry}
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-display font-semibold text-foreground mb-1">
              {caseStudy.company}
            </h3>
            <p className="text-sm text-muted-foreground">
              {caseStudy.companySize} • {caseStudy.useCase}
            </p>
          </div>
          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
            <Icon name="ClockIcon" size={14} />
            <span>{caseStudy.timeline}</span>
          </div>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Challenge</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {caseStudy.challenge}
          </p>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Solution</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {caseStudy.solution}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          {caseStudy.metrics.slice(0, 2).map((metric, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3">
              <div className="text-lg font-display font-semibold text-primary mb-1">
                {metric.value}
              </div>
              <div className="text-xs text-muted-foreground mb-1">
                {metric.label}
              </div>
              <div className="flex items-center space-x-1 text-xs text-success">
                <Icon name="ArrowTrendingUpIcon" size={12} />
                <span>{metric.improvement}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-muted/30 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-2 mb-2">
            <Icon name="ChatBubbleLeftRightIcon" size={16} className="text-primary mt-1 flex-shrink-0" />
            <p className="text-sm text-foreground italic leading-relaxed">
              "{caseStudy.executiveQuote}"
            </p>
          </div>
          <div className="text-xs text-muted-foreground ml-6">
            — {caseStudy.executiveName}, {caseStudy.executiveTitle}
          </div>
        </div>

        <button
          onClick={() => onViewDetails(caseStudy.id)}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium text-sm rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5"
        >
          <span>View Full Case Study</span>
          <Icon name="ArrowRightIcon" size={16} />
        </button>
      </div>
    </div>
  );
};

export default CaseStudyCard;